import { useCallback, useMemo, useRef, useState } from 'react';
import { BiExport, BiPlus, BiReceipt } from 'react-icons/bi';
import { CgImport } from 'react-icons/cg';
import { FaFileCsv, FaFilePdf } from 'react-icons/fa6';
import { FiTag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { ConfirmationModal, LoaderSpinner } from '@/components';
import { ButtonPill, ButtonPillPrimary, ButtonSmall } from '@/components/Button';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { EmptyPage } from '@/components/EmptyPage';
import { useImportedTransactionsStore } from '@/store/expenses-store/transactions/imported-transactions.store.ts';
import type {
  Transaction,
  TransactionRow,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import ErrorPage from '@pages/Expenses/components/ErrorPage/ErrorPage.tsx';
import { ExpensesToolbar, type FilterGroup } from '@pages/Expenses/components/ExpensesToolbar.tsx';
import { ManageCategoriesModal } from '@pages/Expenses/components/modals/ManageCategoriesModal.tsx';
import TransactionModal from '@pages/Expenses/components/modals/TransactionModal';
import TransactionsTable from '@pages/Expenses/components/TransactionsTable.tsx';
import styles from '@pages/Expenses/TransactionsPage/blocks/DesktopTransactions.module.scss';
import {
  AMOUNT_OPTIONS,
  DATE_OPTIONS,
  DEFAULT_FILTERS,
  matchesDate,
  SEGMENT_EMPTY_STATE,
} from '@pages/Expenses/TransactionsPage/transaction.constants.ts';
import type {
  TransactionSegment,
  TransactionsProps,
} from '@pages/Expenses/TransactionsPage/transactions.types.ts';
import {
  formatSignedAmount,
  isInflow,
} from '@pages/Expenses/TransactionsPage/utils/transactionColors';
import { useCategoryFilterParam } from '@pages/Expenses/TransactionsPage/utils/useCategoryFilterParam';
import { useTransactionApprovals } from '@pages/Expenses/TransactionsPage/utils/useTransactionApprovals.ts';

const matchesAmount = (amount: number, filter: string) => {
  if (filter === 'under-1000') return amount < 1000;
  if (filter === '1000-5000') return amount >= 1000 && amount <= 5000;
  if (filter === 'over-5000') return amount > 5000;
  return true;
};

export const DesktopTransactions = ({
  data,
  expenses,
  loading,
  isCsvImporting,
  error,
  canManage,
  canManageCategories,
}: TransactionsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Transaction | null>(null);
  const [startInEdit, setStartInEdit] = useState(false);
  const [segment, setSegment] = useState<TransactionSegment>('all');
  const [filters, setFilters] = useState<Record<string, string>>(DEFAULT_FILTERS);
  const importInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [exportButtonEl, setExportButtonEl] = useState<HTMLButtonElement | null>(null);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<TransactionRow | null>(null);

  const { transactionCategories, removeTransaction, getAllTransactions } = useTransactionsStore();
  const { segments } = useTransactionApprovals();
  const { importTransactionFile, importLoading } = useImportedTransactionsStore();
  const exportTransactions = useTransactionsStore((state) => state.exportTransactions);
  const exportLoading = useTransactionsStore((state) => state.exportLoading);

  const applyCategoryFilter = useCallback(
    (categoryTitle: string) => setFilters((prev) => ({ ...prev, category: categoryTitle })),
    [],
  );

  useCategoryFilterParam(transactionCategories, applyCategoryFilter);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      { key: 'date', title: 'Date', options: DATE_OPTIONS },
      {
        key: 'category',
        title: 'Category',
        options: [
          { value: 'all', label: 'All' },
          ...transactionCategories.map((category) => ({
            value: category.title,
            label: category.title,
          })),
        ],
      },
      { key: 'amount', title: 'Amount', options: AMOUNT_OPTIONS },
    ],
    [transactionCategories],
  );

  const filteredData = useMemo(
    () =>
      data.filter((row) => {
        const inflow = isInflow(row.transactionType);
        if (segment === 'approvals' && row.status !== 'needs_approval') return false;
        if (segment === 'payments' && inflow) return false;
        if (segment === 'refunds' && !inflow) return false;
        if (!matchesDate(row.date, filters.date)) return false;
        if (filters.category !== 'all' && row.type !== filters.category) return false;
        return matchesAmount(row.amount, filters.amount);
      }),
    [data, segment, filters],
  );

  const emptyState = SEGMENT_EMPTY_STATE[segment];

  const openExpense = (id: string, edit = false) => {
    const expense = expenses?.find((e) => e.id === id);
    if (!expense) return;
    setSelectedExpense(expense);
    setStartInEdit(edit);
    setIsModalOpen(true);
  };

  const handleRowClick = (row: TransactionRow) => openExpense(row.id);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExpense(null);
  };

  const handleSegmentChange = (value: TransactionSegment) => {
    setSegment(value);
    setFilters(DEFAULT_FILTERS);
  };

  const handleAddTransaction = () => {
    setSelectedExpense(null);
    setStartInEdit(false);
    setIsModalOpen(true);
  };

  const handleImportFile = async (file: File | undefined) => {
    if (!file) return;
    const isSuccess = await importTransactionFile(file);
    if (isSuccess) navigate('/manage/transactions/imported');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.actions}>
        <ExpensesToolbar
          segments={segments}
          segment={segment}
          onSegmentChange={handleSegmentChange}
          filterGroups={filterGroups}
          filterValues={filters}
          onFilterChange={(groupKey, value) =>
            setFilters((prev) => ({ ...prev, [groupKey]: value }))
          }
          onClearFilters={() => setFilters(DEFAULT_FILTERS)}
          actions={
            <>
              <ButtonPill
                ref={setExportButtonEl}
                disabled={exportLoading}
                aria-haspopup="menu"
                aria-expanded={isExportMenuOpen}
                onClick={() => setIsExportMenuOpen((prev) => !prev)}
              >
                <BiExport aria-hidden="true" /> {exportLoading ? 'Exporting…' : 'Export'}
              </ButtonPill>
              {canManageCategories && (
                <ButtonPill onClick={() => setIsCategoriesModalOpen(true)}>
                  <FiTag aria-hidden="true" /> Manage Categories
                </ButtonPill>
              )}
              {canManage && (
                <>
                  <ButtonPill
                    disabled={isCsvImporting || importLoading}
                    onClick={() => importInputRef.current?.click()}
                  >
                    <CgImport aria-hidden="true" />{' '}
                    {isCsvImporting || importLoading ? 'Importing…' : 'Import'}
                  </ButtonPill>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf"
                    hidden
                    onChange={(event) => {
                      void handleImportFile(event.target.files?.[0]);
                      event.target.value = '';
                    }}
                  />
                  <ButtonPillPrimary onClick={handleAddTransaction}>
                    <BiPlus aria-hidden="true" /> Add
                  </ButtonPillPrimary>
                </>
              )}
            </>
          }
        />
      </div>

      <div className={styles.content}>
        {loading && <LoaderSpinner />}
        {error && <ErrorPage error={error} />}
        {!loading &&
          !error &&
          (filteredData.length === 0 ? (
            <EmptyPage
              icon={<BiReceipt size={64} />}
              title={emptyState.title}
              desc={emptyState.desc}
              action={
                canManage && emptyState.canAdd ? (
                  <ButtonSmall onClick={handleAddTransaction}>Add</ButtonSmall>
                ) : undefined
              }
            />
          ) : (
            <TransactionsTable
              data={filteredData}
              onRowClick={handleRowClick}
              onEdit={canManage ? (row) => openExpense(row.id, true) : undefined}
              onDelete={canManage ? (row) => setPendingDeleteRow(row) : undefined}
            />
          ))}
      </div>

      <ContextMenu
        isOpen={isExportMenuOpen}
        onClose={() => setIsExportMenuOpen(false)}
        anchorEl={exportButtonEl}
        align="center"
        items={[
          {
            label: 'Export PDF',
            icon: <FaFilePdf />,
            onClick: () => void exportTransactions('pdf'),
          },
          {
            label: 'Export CSV',
            icon: <FaFileCsv />,
            onClick: () => void exportTransactions('csv'),
          },
        ]}
      />

      <ManageCategoriesModal
        open={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        onChanged={getAllTransactions}
      />

      <TransactionModal
        key={`${selectedExpense?.id ?? 'new'}-${startInEdit}`}
        startInEdit={startInEdit}
        open={isModalOpen}
        onClose={handleCloseModal}
        expense={selectedExpense}
        isExist={!!selectedExpense}
        canManage={canManage}
      />

      <ConfirmationModal
        open={pendingDeleteRow !== null}
        onClose={() => setPendingDeleteRow(null)}
        onConfirm={() => {
          if (pendingDeleteRow) void removeTransaction(pendingDeleteRow.id);
        }}
        title="Delete Transaction"
        message={
          pendingDeleteRow
            ? pendingDeleteRow.reason
              ? `Would you like to delete the transaction from ${pendingDeleteRow.date}, “${pendingDeleteRow.reason}” (${formatSignedAmount(pendingDeleteRow.amount, pendingDeleteRow.transactionType)})? This can’t be undone.`
              : `Would you like to delete the transaction from ${pendingDeleteRow.date} (${formatSignedAmount(pendingDeleteRow.amount, pendingDeleteRow.transactionType)})? This can’t be undone.`
            : ''
        }
      />
    </div>
  );
};
